import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ApiClient from '../config/ApiClient';
import CorrelationSection from './team-report/CorrelationSection';
import DomainRankingsSection from './team-report/DomainRankingsSection';
import RecommendationsSection from './team-report/RecommendationsSection';
import SpikeAnalysisSection from './team-report/SpikeAnalysisSection';
import TeamReportHeader from './team-report/TeamReportHeader';
import TierSection from './team-report/TierSection';
import { TeamReportData } from './team-report/types';
import VarianceSection from './team-report/VarianceSection';

const TeamReportPage: React.FC = () => {
    const [data, setData] = useState<TeamReportData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        ApiClient.get<TeamReportData>('/api/team-report')
            .then((res) => {
                setData(res.data as TeamReportData);
            })
            .catch((err) => {
                const msg = err?.response?.data?.detail || 'Failed to load report data';
                setError(msg);
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!data) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">No report data available.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', p: { xs: 2, md: 3 } }} className="team-report">
            <TeamReportHeader
                organizationName={data.header.organization_name}
                reportDate={data.header.report_date}
                submissionCount={data.header.submission_count}
            />
            <DomainRankingsSection data={data.domain_rankings} />
            <VarianceSection data={data.variance} />
            <SpikeAnalysisSection data={data.spike_analysis} />
            <TierSection data={data.tier_segmentation} />
            {data.domain_correlation && (
                <CorrelationSection data={data.domain_correlation} />
            )}
            <RecommendationsSection recommendations={data.recommendations} />
        </Box>
    );
};

export default TeamReportPage;
